import {EmployeeRoleLevel, JiraObject} from 'client';
import {isDev, isStage} from 'helpers/environment';

export const MOCK_JIRA_TENANT_ID = 'mock-jira';

const MOCK_JIRA_STATUSES = ['On Track', 'At  Risk', 'Done'] as const;
const MOCK_JIRA_DATE_STRINGS = [
  '2026-05-02T00:00:00.000Z',
  '2026-05-16T00:00:00.000Z',
  '2026-03-28T00:00:00.000Z',
] as const;
export const shouldUseMockJiraObjects = (jiraObjects: JiraObject[], isError = false) =>
  (isDev || isStage) && (isError || jiraObjects.length === 0);

export const isMockJiraObject = (item: JiraObject) => item.tenant_id === MOCK_JIRA_TENANT_ID;

export type MockJiraWorkstream = {
  id: number;
  name?: string | null;
  archived?: boolean;
  employee_associations?: Array<{
    dri?: string | null;
    employee: EmployeeRoleLevel;
  }>;
  dependencies?: Array<{
    dependent: MockJiraWorkstream;
  }>;
};

export type MockJiraParentWorkstream = MockJiraWorkstream;

export const getMockWorkstreamJiraObjects = (workstream: MockJiraWorkstream): JiraObject[] => {
  const workstreamLabel = workstream.name?.trim() || `Workstream ${workstream.id}`;
  const workstreamOwner =
    workstream.employee_associations?.find(({dri}) => dri === 'dri')?.employee ??
    workstream.employee_associations?.[0]?.employee ??
    null;
  const mockTitles = [
    `Define rollout milestones for ${workstreamLabel}`,
    `Pilot ${workstreamLabel} with instrumentation and issue triage`,
    `Close launch blockers for ${workstreamLabel}`,
  ];
  const mockUpdates = [
    'Design review is complete and engineering sizing is underway.',
    'Pilot scope is defined; the team is working through a small set of follow-up risks.',
    'Final cleanup and rollout prep are complete.',
  ];

  return mockTitles.map((title, index) => ({
    id: -(workstream.id * 10 + index + 1),
    key: `PULSE-${workstream.id * 10 + index + 1}`,
    title,
    complete_date: MOCK_JIRA_DATE_STRINGS[index],
    lifecycle: 'initiative',
    status: MOCK_JIRA_STATUSES[index],
    latest_update: mockUpdates[index],
    tenant_id: MOCK_JIRA_TENANT_ID,
    owner: workstreamOwner,
    workstream_id: workstream.id,
    created_at: '2026-04-01T00:00:00.000Z',
    updated_at: '2026-04-01T00:00:00.000Z',
  }));
};

export const getMockElevatableJiraObjects = async (
  workstream: MockJiraParentWorkstream,
  loadWorkstream?: (workstreamId: number) => Promise<MockJiraParentWorkstream>
): Promise<JiraObject[]> => {
  const visitedWorkstreamIds = new Set<number>([workstream.id]);
  let frontier = (workstream.dependencies ?? []).map(({dependent}) => dependent);
  const candidates: JiraObject[] = [];

  while (frontier.length > 0) {
    const nextWorkstreams = frontier.filter((candidate) => {
      if (visitedWorkstreamIds.has(candidate.id)) {
        return false;
      }
      visitedWorkstreamIds.add(candidate.id);
      return candidate.name !== 'Restricted';
    });
    const resolvedWorkstreams = await Promise.all(
      nextWorkstreams.map((candidate) =>
        candidate.dependencies !== undefined || !loadWorkstream
          ? Promise.resolve(candidate)
          : loadWorkstream(candidate.id)
      )
    );

    for (const candidateWorkstream of resolvedWorkstreams) {
      if (!candidateWorkstream.archived && candidateWorkstream.name !== 'Restricted') {
        candidates.push(
          ...getMockWorkstreamJiraObjects(candidateWorkstream).filter(
            (item) => !['completed', 'done'].includes((item.status ?? '').trim().toLowerCase())
          )
        );
      }
    }

    frontier = resolvedWorkstreams.flatMap((candidate) =>
      (candidate.dependencies ?? []).map(({dependent}) => dependent)
    );
  }

  return candidates;
};
